import {
  DEFAULT_PANE_HEIGHT,
  DEFAULT_PANE_WIDTH,
  DEFAULT_TEMPLATE_PANE_HEIGHT,
  DEFAULT_TEMPLATE_PANE_WIDTH,
} from "./Constants.js";
import {
  Action,
  ActionType,
  ConstructedPane,
  Pane,
  PaneLayoutPaneMetadata,
  PaneType,
  Position,
  Rect,
  SupportedBabelPlugin,
} from "./Types.js";

export function assert(condition: any, msg?: string): asserts condition {
  if (!condition) {
    throw new Error(msg);
  }
}

export function removeUuidDashes(uuid: string) {
  return uuid.replace(/-/g, "");
}

export function getNewPaneRect(
  paneType: PaneType,
  position: Position,
  size?: Position
): Rect {
  return [
    position[0],
    position[1],
    size !== undefined
      ? size[0]
      : paneType === PaneType.EvaluateTemplate
      ? DEFAULT_TEMPLATE_PANE_WIDTH
      : paneType === PaneType.Import || paneType === PaneType.EvaluateGlobal
      ? 352
      : DEFAULT_PANE_WIDTH,
    size !== undefined
      ? size[1]
      : paneType === PaneType.EvaluateTemplate
      ? DEFAULT_TEMPLATE_PANE_HEIGHT
      : DEFAULT_PANE_HEIGHT,
  ];
}

export const PANE_KEY_PREFIX = "p/";
export const LAYOUT_KEY_PREFIX = "l/";
export function getPaneKey(paneId?: string) {
  return `${PANE_KEY_PREFIX}${paneId ?? ""}`;
}
export function getLayoutKey(layoutId?: string) {
  return `${LAYOUT_KEY_PREFIX}${layoutId ?? ""}`;
}

export function getNewPane(
  paneFields: { id: string; rect: Rect; z: number; name: string | undefined },
  paneType: PaneType
): Pane {
  switch (paneType) {
    case PaneType.Evaluate:
      return {
        ...paneFields,
        inputs: [],
        type: PaneType.Evaluate,
        autorun: true,
        expression: "",
        editorHeight: undefined,
        babelPlugins: [],
      };
    case PaneType.EvaluateGlobal:
      return {
        ...paneFields,
        type: PaneType.EvaluateGlobal,
        autorun: true,
        expression: "",
        editorHeight: undefined,
      };
    case PaneType.InputText:
      return {
        ...paneFields,
        type: PaneType.InputText,
        text: "",
      };
    case PaneType.State:
      return {
        ...paneFields,
        type: PaneType.State,
        initialExpression: "",
      };
    case PaneType.Import:
      return {
        ...paneFields,
        type: PaneType.Import,
        module: undefined,
        isGlobal: false,
        useDefault: false,
      };
    case PaneType.EnvironmentVariable:
      return {
        ...paneFields,
        type: PaneType.EnvironmentVariable,
        key: undefined,
      };
  }
  throw new Error();
}

export function createMissingPaneLayoutPaneMetadata(
  pane: Pane
): PaneLayoutPaneMetadata {
  const paneMetadata: PaneLayoutPaneMetadata = {
    rect: [...pane.rect],
    z: pane.z,
  };
  if (
    pane.type === PaneType.Evaluate ||
    pane.type === PaneType.EvaluateGlobal ||
    pane.type === PaneType.EvaluateTemplate ||
    pane.type === PaneType.State
  ) {
    paneMetadata.editorHeight = pane.editorHeight;
  }
  return paneMetadata;
}

export function areBabelPluginsEqual(
  a: SupportedBabelPlugin,
  b: SupportedBabelPlugin
) {
  if (a === b) {
    return true;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a[1] === b[1];
  }
  return false;
}

export function isPresenceAction(action: Action) {
  return (
    action[0] === ActionType.PresenceCursor ||
    action[0] === ActionType.PresencePaneId ||
    action[0] === ActionType.PresenceTextRange ||
    action[0] === ActionType.PresenceName
  );
}

// (abcdefghijklmnopqrstuvwxyz, 12) => abcdefghijklmnopqrstuvwx12
export function getEjectedTemplatePaneId(
  originalPaneId: string,
  paneIndex: number
) {
  const paneIndexString = `${paneIndex}`;
  return `${originalPaneId.substring(
    0,
    originalPaneId.length - paneIndexString.length
  )}${paneIndexString}`;
}

// (abcdefgijklmn, 12) => abcdef12
export function getEjectedTemplatePaneInputId(
  paneId: string,
  inputIndex: number
): string {
  const inputIndexString = `${inputIndex}`;
  return `${paneId.substring(
    0,
    8 - inputIndexString.length
  )}${inputIndexString}`;
}

export function transformRect(rect: Rect, x: number, y: number): Rect {
  return [rect[0] + x, rect[1] + y, rect[2], rect[3]];
}

export function createEjectedPanes(
  templatePane: Pane,
  templateInputSources: Record<string, [paneId: string, outputIndex: number]>,
  constructedPanes: ConstructedPane[]
): Pane[] {
  const templatePaneId = templatePane.id;
  return constructedPanes.map((constructedPane, paneIndex) => {
    switch (constructedPane.type) {
      case PaneType.Evaluate: {
        return {
          id: getEjectedTemplatePaneId(templatePaneId, paneIndex),
          type: PaneType.Evaluate,
          rect:
            constructedPane.rect !== undefined
              ? transformRect(
                  constructedPane.rect,
                  templatePane.rect[0],
                  templatePane.rect[1]
                )
              : templatePane.rect,
          z: templatePane.z + (constructedPane.z ?? 0),
          name: paneIndex === 0 ? templatePane.name : constructedPane.name,
          inputs: constructedPane.inputs.map((paneInput, inputIndex) => ({
            id: getEjectedTemplatePaneInputId(templatePaneId, inputIndex),
            name: paneInput.name,
            source: Array.isArray(paneInput.source)
              ? [
                  getEjectedTemplatePaneId(templatePaneId, paneInput.source[0]),
                  paneInput.source[1] ?? 0,
                ]
              : templateInputSources[paneInput.source],
          })),
          expression: constructedPane.expression,
          editorHeight: undefined,
          autorun: true,
          expressionType: constructedPane.expressionType,
          babelPlugins: [],
          renderOutput: constructedPane.renderOutput,
        };
      }
      case PaneType.Import: {
        return {
          id: getEjectedTemplatePaneId(templatePaneId, paneIndex),
          type: PaneType.Import,
          rect:
            constructedPane.rect !== undefined
              ? transformRect(
                  constructedPane.rect,
                  templatePane.rect[0],
                  templatePane.rect[1]
                )
              : templatePane.rect,
          z: templatePane.z + (constructedPane.z ?? 0),
          name: constructedPane.name,
          module: constructedPane.module,
        };
      }
      case PaneType.State: {
        return {
          id: getEjectedTemplatePaneId(templatePaneId, paneIndex),
          type: PaneType.State,
          rect:
            constructedPane.rect !== undefined
              ? transformRect(
                  constructedPane.rect,
                  templatePane.rect[0],
                  templatePane.rect[1]
                )
              : templatePane.rect,
          z: templatePane.z + (constructedPane.z ?? 0),
          name: constructedPane.name,
          initialExpression: constructedPane.initialExpression,
          control: constructedPane.control,
        };
      }
    }
  });
}
